import { CardElement, Elements, useElements, useStripe } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { useState } from 'react';
import env from '../../config/env';
import { useAxios } from '../../utils/hook/useAxios';

/*
 * Styles : mc-guest-settings.css (importé par la page de paramètres).
 *
 * La confirmation visuelle (modal + statut) est gérée par le parent via
 * `onCardSave(result)` : ce composant est démonté dès que la carte est
 * enregistrée, il ne doit donc pas porter lui-même le modal.
 */

const stripePromise = loadStripe(env.stripePublicKey);

const CARD_OPTIONS = {
  style: {
    base: {
      fontFamily: 'Inter, system-ui, -apple-system, sans-serif',
      fontSize: '15px',
      fontWeight: '600',
      color: '#16213e',
      '::placeholder': { color: '#98a2b3', fontWeight: '500' },
    },
    invalid: { color: '#b42318' },
  },
};

const PaymentForm = ({ onCardSave }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [errorMessage, setErrorMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const axios = useAxios();

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!stripe || !elements) return;

    setLoading(true);
    setErrorMessage('');

    try {
      // 1. Créer un SetupIntent côté serveur
      const { data } = await axios.post('/stripe/create-setup-intent');
      const clientSecret = data.clientSecret;

      // 2. Confirmer la sauvegarde de la carte via Stripe (pas de débit)
      const cardElement = elements.getElement(CardElement);
      const { setupIntent, error } = await stripe.confirmCardSetup(clientSecret, {
        payment_method: { card: cardElement },
      });

      if (error) {
        const messages = {
          card_declined: 'Carte refusée. Veuillez essayer une autre carte.',
          insufficient_funds: 'Fonds insuffisants sur cette carte.',
          expired_card: 'Cette carte est expirée.',
          incorrect_cvc: 'Le code de sécurité (CVC) est incorrect.',
          resource_missing: 'Session expirée. Veuillez recharger la page et réessayer.',
          setup_intent_authentication_failure: 'Authentification échouée. Veuillez réessayer.',
        };
        setErrorMessage(messages[error.code] || 'Une erreur est survenue. Veuillez réessayer.');
        return;
      }

      // Le SetupIntent doit être "succeeded" pour que la carte soit enregistrée
      if (setupIntent?.status !== 'succeeded') {
        setErrorMessage("La carte n'a pas pu être validée. Veuillez réessayer.");
        return;
      }

      // 3. Informer le serveur pour sauvegarder last4/exp en BDD
      const { data: saved } = await axios.post('/stripe/confirm-card-setup', {
        paymentMethodId: setupIntent.payment_method,
      });

      // 4. Le parent affiche le modal de confirmation et met le statut à jour
      if (onCardSave) await onCardSave(saved);
    } catch (err) {
      setErrorMessage(err.response?.data?.error || "Une erreur s'est produite");
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <div className="mc-gs-stripe-box">
        <CardElement options={CARD_OPTIONS} />
      </div>

      <p className="mc-gs-secure">
        <i className="bi bi-shield-lock-fill" aria-hidden="true"></i>
        Paiement sécurisé par Stripe. Aucun débit lors de l'enregistrement.
      </p>

      {errorMessage && (
        <div className="mc-gs-error" role="alert">
          <i className="bi bi-exclamation-circle-fill" aria-hidden="true"></i>
          <span>{errorMessage}</span>
        </div>
      )}

      <button
        type="submit"
        className="mc-btn mc-btn--primary mc-gs-submit"
        disabled={!stripe || loading}
      >
        {loading ? (
          <>
            <span className="mc-gs-spin" aria-hidden="true"></span>
            Enregistrement…
          </>
        ) : (
          <>
            <i className="bi bi-credit-card" aria-hidden="true"></i>
            Enregistrer la carte
          </>
        )}
      </button>
    </form>
  );
};

const PaymentPage = ({ onCardSave }) => (
  <Elements stripe={stripePromise}>
    <PaymentForm onCardSave={onCardSave} />
  </Elements>
);

export default PaymentPage;
